import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';

import { ModernButton } from '../../components/ui/ModernButton';
import { StatusIndicator, CustomerTagsDisplay } from '../../components/ui/ModernIndicators';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CustomerService } from '../../services/customerService';
import { Customer, CustomerStatus } from '../../types/agent';
import { TableErrorBoundary } from '../../components/ErrorBoundary';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import './CustomersPage.css';

export const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentAgent } = useAuth();
  const toast = useRef<Toast>(null);
  const [searchParams] = useSearchParams();
  const { handleAsyncOperation } = useErrorHandler(toast);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);

  // Cursor paging. This page used to request limit: 1000 and filter in the
  // browser, which shipped every customer record over the wire and silently
  // dropped anyone sorting past the thousandth. It now loads a page at a time
  // and says so when more remain.
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50;

  // Filters
  const [statusFilter, setStatusFilter] = useState<CustomerStatus | 'all'>('all');
  const [leadScoreRange, setLeadScoreRange] = useState<[number, number]>([0, 100]);
  const [tagsFilter, setTagsFilter] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Prospect', value: 'prospect' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Converted', value: 'converted' }
  ];

	/**
	 * Status and search are sent to the server; lead score and tags stay local
	 * because the backend has no index for them. Local filters therefore apply
	 * to what has been loaded so far, which is why "Load more" is explicit
	 * rather than hidden behind the table's paginator.
	 */
	const scopeFilters = useCallback(() => (
		(currentAgent?.role === 'master_agent' || currentAgent?.role === 'super_agent')
			? {}
			: { assignedAgentId: currentAgent?.id }
	), [currentAgent]);

	const collectTags = (list: Customer[]) => {
		const tags = new Set<string>();
		list.forEach(customer => customer.tags?.forEach(tag => tags.add(tag)));
		setAvailableTags(Array.from(tags));
	};

	const loadCustomers = useCallback(async () => {
		setLoading(true);
		const result = await handleAsyncOperation(async () => (
			CustomerService.getCustomers({
				...scopeFilters(),
				...(statusFilter !== 'all' ? { status: statusFilter } : {}),
				...(globalFilter ? { searchTerm: globalFilter } : {}),
				limit: PAGE_SIZE
			})
		), {
			fallbackMessage: 'Failed to load customers. Please try again.'
		});

		if (result) {
			setCustomers(result.customers);
			setCursor(result.nextCursor);
			setHasMore(result.hasMore);
			collectTags(result.customers);
		}

		setLoading(false);
	}, [scopeFilters, statusFilter, globalFilter, handleAsyncOperation]);

	const loadMore = useCallback(async () => {
		if (!hasMore || loadingMore) return;
		setLoadingMore(true);

		const result = await handleAsyncOperation(async () => (
			CustomerService.getCustomers({
				...scopeFilters(),
				...(statusFilter !== 'all' ? { status: statusFilter } : {}),
				...(globalFilter ? { searchTerm: globalFilter } : {}),
				limit: PAGE_SIZE,
				cursor
			})
		), {
			fallbackMessage: 'Failed to load more customers. Please try again.'
		});

		if (result) {
			setCustomers(previous => {
				const combined = [...previous, ...result.customers];
				collectTags(combined);
				return combined;
			});
			setCursor(result.nextCursor);
			setHasMore(result.hasMore);
		}

		setLoadingMore(false);
	}, [hasMore, loadingMore, cursor, scopeFilters, statusFilter, globalFilter, handleAsyncOperation]);

  // Debounced because status and search now go to the server - without this,
  // every keystroke would be a round trip.
  useEffect(() => {
    const timer = setTimeout(() => { loadCustomers(); }, 300);
    return () => clearTimeout(timer);
  }, [loadCustomers]);

  // Seed the search box from a notification deep link. Runs only when that
  // parameter changes, so the toast no longer re-fires on unrelated reloads.
  useEffect(() => {
    const searchTerm = searchParams.get('search');
    if (!searchTerm) return;

    setGlobalFilter(searchTerm);
    toast.current?.show({
      severity: 'info',
      summary: 'Customer Located',
      detail: `Showing results for: ${searchTerm}`,
      life: 3000
    });
  }, [searchParams]);

  const filteredCustomers = customers.filter(customer => {
    // Status filter
    if (statusFilter !== 'all' && customer.status !== statusFilter) {
      return false;
    }

    // Lead score filter
    const score = customer.leadScore || 0;
    if (score < leadScoreRange[0] || score > leadScoreRange[1]) {
      return false;
    }

    // Tags filter
    if (tagsFilter.length > 0) {
      const hasAllTags = tagsFilter.every(tag =>
        customer.tags?.includes(tag)
      );
      if (!hasAllTags) return false;
    }

    // Global search filter
    if (globalFilter) {
      const searchLower = globalFilter.toLowerCase();
      const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();
      return fullName.includes(searchLower) ||
             customer.email.toLowerCase().includes(searchLower) ||
             customer.phone?.includes(globalFilter) ||
             customer.notes?.toLowerCase().includes(searchLower);
    }

    return true;
  });

  const handleDeleteCustomer = (customer: Customer) => {
    confirmDialog({
      message: `Are you sure you want to delete ${customer.firstName} ${customer.lastName}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await CustomerService.deleteCustomer(customer.id);
          await loadCustomers();
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Customer deleted successfully',
            life: 3000
          });
        } catch (error) {
          console.error('Error deleting customer:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete customer',
            life: 3000
          });
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedCustomers.length === 0) return;

    confirmDialog({
      message: `Are you sure you want to delete ${selectedCustomers.length} customers?`,
      header: 'Confirm Bulk Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          const promises = selectedCustomers.map(customer =>
            CustomerService.deleteCustomer(customer.id)
          );
          await Promise.all(promises);
          await loadCustomers();
          setSelectedCustomers([]);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: `${selectedCustomers.length} customers deleted successfully`,
            life: 3000
          });
        } catch (error) {
          console.error('Error deleting customers:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete some customers',
            life: 3000
          });
        }
      }
    });
  };

  const handleViewCustomer = (customer: Customer) => {
    navigate(`/agent/customers/${customer.id}`);
  };

  const handleEditCustomer = (customer: Customer) => {
    navigate(`/agent/customers/${customer.id}/edit`);
  };

  // RFC 4180: wrap every field in quotes and double any embedded quote, so
  // commas, quotes and newlines inside a value cannot break the file
  const escapeCsvValue = (value: unknown): string => {
    const stringValue = value === null || value === undefined ? '' : String(value);
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const exportCSV = () => {
    const csvData = filteredCustomers.map(customer => ({
      'First Name': customer.firstName,
      'Last Name': customer.lastName,
      'Email': customer.email,
      'Phone': customer.phone || '',
      'Status': customer.status,
      'Lead Score': customer.leadScore || 0,
      'Created Date': new Date(customer.createdAt).toLocaleDateString(),
      'Last Contact': customer.lastContactAt ? new Date(customer.lastContactAt).toLocaleDateString() : '',
      'Tags': customer.tags?.join(', ') || ''
    }));

    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.map(escapeCsvValue).join(','),
      ...csvData.map(row => headers.map(header => escapeCsvValue((row as any)[header])).join(','))
    ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.current?.show({
      severity: 'success',
      summary: 'Export Complete',
      detail: `Exported ${filteredCustomers.length} customers to CSV`,
      life: 3000
    });
  };

  // Column templates
  const nameBodyTemplate = (rowData: Customer) => {
    return (
      <div className="customer-name">
        <span className="name">{rowData.firstName} {rowData.lastName}</span>
      </div>
    );
  };

  const contactBodyTemplate = (rowData: Customer) => {
    return (
      <div className="contact-info">
        <div className="email">{rowData.email}</div>
        {rowData.phone && <div className="phone">{rowData.phone}</div>}
      </div>
    );
  };

  const statusBodyTemplate = (rowData: Customer) => {
    const getStatus = (status: CustomerStatus) => {
      switch (status) {
        case 'converted': return 'completed';
        case 'active': return 'active';
        case 'prospect': return 'pending';
        case 'inactive': return 'inactive';
        default: return 'inactive';
      }
    };

    return (
      <StatusIndicator
        status={getStatus(rowData.status)}
        label={rowData.status.toUpperCase()}
      />
    );
  };

  const leadScoreBodyTemplate = (rowData: Customer) => {
    const score = rowData.leadScore || 0;
    const getScoreClass = (score: number) => {
      if (score >= 80) return 'high';
      if (score >= 50) return 'medium';
      return 'low';
    };

    return (
      <div className={`lead-score ${getScoreClass(score)}`}>
        <span>{score}</span>
        <div className="score-bar">
          <div className="score-fill" style={{ width: `${score}%` }} />
        </div>
      </div>
    );
  };

  const tagsBodyTemplate = (rowData: Customer) => {
    return <CustomerTagsDisplay tags={rowData.tags || []} maxVisible={3} />;
  };

  const actionsBodyTemplate = (rowData: Customer) => {
    return (
      <div className="action-buttons">
        <ModernButton
          variant="ghost"
          size="sm"
          icon="pi pi-eye"
          onClick={() => handleViewCustomer(rowData)}
          title="View customer"
        />
        <ModernButton
          variant="ghost"
          size="sm"
          icon="pi pi-pencil"
          onClick={() => handleEditCustomer(rowData)}
          title="Edit customer"
        />
        <ModernButton
          variant="ghost"
          size="sm"
          icon="pi pi-trash"
          onClick={() => handleDeleteCustomer(rowData)}
          title="Delete customer"
        />
      </div>
    );
  };

  const header = (
    <div className="table-header">
      <div className="header-left">
        <h3>Customers ({filteredCustomers.length})</h3>
        <span className="pi pi-search search-icon" />
        <InputText
          type="search"
          placeholder="Search customers..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="search-input"
        />
      </div>
      <div className="header-right">
        <ModernButton
          variant="ghost"
          icon="pi pi-download"
          onClick={exportCSV}
          disabled={filteredCustomers.length === 0}
        >
          Export
        </ModernButton>
        {selectedCustomers.length > 0 && (
          <ModernButton
            variant="danger"
            icon="pi pi-trash"
            onClick={handleBulkDelete}
          >
            Delete ({selectedCustomers.length})
          </ModernButton>
        )}
        <ModernButton
          variant="primary"
          icon="pi pi-plus"
          onClick={() => navigate('/agent/customers/add')}
        >
          Add Customer
        </ModernButton>
      </div>
    </div>
  );

  return (
    <div className="customers-page">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="page-header">
        <h1>Customer Management</h1>
        <p>View and manage your customer pipeline</p>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <div className="filters-container">
          <div className="filter-item">
            <label>Status</label>
            <Dropdown
              value={statusFilter}
              options={statusOptions}
              onChange={(e) => setStatusFilter(e.value)}
              placeholder="All Statuses"
            />
          </div>

          <div className="filter-item">
            <label>Lead Score Range</label>
            <div className="score-range">
              <InputText
                type="number"
                value={leadScoreRange[0].toString()}
                onChange={(e) => setLeadScoreRange([parseInt(e.target.value) || 0, leadScoreRange[1]])}
                placeholder="Min"
                className="range-input"
              />
              <span>-</span>
              <InputText
                type="number"
                value={leadScoreRange[1].toString()}
                onChange={(e) => setLeadScoreRange([leadScoreRange[0], parseInt(e.target.value) || 100])}
                placeholder="Max"
                className="range-input"
              />
            </div>
          </div>

          <div className="filter-item">
            <label>Tags</label>
            <MultiSelect
              value={tagsFilter}
              options={availableTags.map(tag => ({ label: tag, value: tag }))}
              onChange={(e) => setTagsFilter(e.value)}
              placeholder="Select Tags"
              maxSelectedLabels={3}
            />
          </div>

          <ModernButton
            variant="ghost"
            onClick={() => {
              setStatusFilter('all');
              setLeadScoreRange([0, 100]);
              setTagsFilter([]);
              setGlobalFilter('');
            }}
          >
            Clear Filters
          </ModernButton>
        </div>
      </Card>

      {/* Customers Table */}
      <Card className="customers-table-card">
        <TableErrorBoundary>
          <DataTable
            value={filteredCustomers}
            loading={loading}
            selection={selectedCustomers}
            onSelectionChange={(e) => setSelectedCustomers(e.value as Customer[])}
            selectionMode="multiple"
            dataKey="id"
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            header={header}
            emptyMessage="No customers found."
            className="customers-table"
          >
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
          <Column
            field="name"
            header="Name"
            body={nameBodyTemplate}
            sortable
          />
          <Column
            field="contact"
            header="Contact"
            body={contactBodyTemplate}
          />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            sortable
          />
          <Column
            field="leadScore"
            header="Lead Score"
            body={leadScoreBodyTemplate}
            sortable
          />
          <Column
            field="tags"
            header="Tags"
            body={tagsBodyTemplate}
          />
          <Column
            field="createdAt"
            header="Created"
            body={(rowData) => new Date(rowData.createdAt).toLocaleDateString()}
            sortable
          />
          <Column
            header="Actions"
            body={actionsBodyTemplate}
            headerStyle={{ width: '10rem', textAlign: 'center' }}
            bodyStyle={{ textAlign: 'center' }}
          />
          </DataTable>
        </TableErrorBoundary>

        {/*
          Explicit rather than automatic. Lead-score and tag filters run against
          what is loaded, so the user needs to know the set is partial before
          concluding a customer is missing.
        */}
        {hasMore && (
          <div className="customers-load-more">
            <span className="customers-load-more-note">
              Showing the first {customers.length} customers.
            </span>
            <ModernButton
              variant="secondary"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </ModernButton>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CustomersPage;
